import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useStore from "../store.js";

const acceptedNoSessionRetryLimit = 3;
const statusPollIntervalMs = 15000;
const recoveryDebounceMs = 80;

const PendingQueryMonitor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location.pathname);
  const {
    cleanupStatusSSE,
    fetchQueryStatus,
    isPendingSearchAccepted,
    startStatusSSE,
    statusMonitorVersion,
  } = useStore();
  const isWaitingPage = location.pathname === "/main/q/waiting";
  const shouldMonitor = isPendingSearchAccepted || isWaitingPage;

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldMonitor) return undefined;

    let disposed = false;
    let noSessionCount = 0;
    let recoveryRunning = false;
    let recoveryQueued = false;
    let retryCount = 0;
    let retryTimer = null;
    let debounceTimer = null;

    const complete = async (logId, completeData) => {
      if (!logId || disposed) return;
      useStore.setState({
        logId,
        queryCompleteData: completeData,
        tempLog: null,
        pendingRefinement: null,
        isLoading: false,
        isPendingSearchAccepted: false,
      });
      await useStore.getState().fetchLogs();
      if (!disposed && locationRef.current === "/main/q/waiting") {
        navigate(`/main/q/${logId}`, { replace: true });
      }
    };

    const restoreWithoutSession = async () => {
      const state = useStore.getState();
      if (state.isPendingSearchAccepted && noSessionCount < acceptedNoSessionRetryLimit) {
        noSessionCount += 1;
        scheduleRecovery(Math.min(1000 * noSessionCount, 3000));
        return true;
      }

      useStore.setState({
        isLoading: false,
        isPendingSearchAccepted: false,
        tempLog: null,
      });
      if (locationRef.current !== "/main/q/waiting") return false;
      await state.fetchLogs();
      if (disposed) return false;
      const logs = useStore.getState().logs;
      const latestLog = [...logs].sort((a, b) => b.id - a.id)[0];
      navigate(latestLog ? `/main/q/${latestLog.id}` : "/main", {
        replace: true,
      });
      return false;
    };

    const handleStreamError = (_error, { retryable = false } = {}) => {
      if (retryable) {
        retryCount += 1;
        scheduleRecovery(Math.min(1000 * (2 ** (retryCount - 1)), 10000));
      }
    };

    const ensureStatusStream = () => {
      const state = useStore.getState();
      if (state.statusSSEController || state.isStatusSSEConnected) return;
      state.startStatusSSE({
        onComplete: complete,
        onNoSession: restoreWithoutSession,
        onError: handleStreamError,
      });
    };

    const recover = async () => {
      if (disposed) return;
      if (document.visibilityState === "hidden") {
        recoveryQueued = true;
        return;
      }
      if (recoveryRunning) {
        recoveryQueued = true;
        return;
      }

      recoveryRunning = true;
      recoveryQueued = false;
      try {
        const snapshot = await fetchQueryStatus();
        if (disposed) return;

        if (snapshot?.status === "processing") {
          retryCount = 0;
          noSessionCount = 0;
          ensureStatusStream();
          return;
        }

        if (snapshot?.status === "completed" && snapshot.log_id) {
          await complete(snapshot.log_id, snapshot);
          return;
        }

        if (snapshot?.status === "error") {
          useStore.setState({
            isLoading: false,
            isPendingSearchAccepted: false,
            sseEventName: "Error",
          });
          cleanupStatusSSE();
          return;
        }

        if (snapshot?.status === "none") {
          await restoreWithoutSession();
        }
      } catch (error) {
        if (disposed) return;
        if (error?.retryable !== false) {
          retryCount += 1;
          scheduleRecovery(Math.min(1000 * (2 ** (retryCount - 1)), 10000));
          return;
        }
        useStore.setState({
          isLoading: false,
          isPendingSearchAccepted: false,
          sseEventName: "Error",
        });
      } finally {
        recoveryRunning = false;
        if (recoveryQueued && !disposed) scheduleRecovery(recoveryDebounceMs);
      }
    };

    function scheduleRecovery(delay = recoveryDebounceMs) {
      if (disposed || retryTimer) return;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        recover();
      }, delay);
    }

    const recoverOnResume = () => {
      if (document.visibilityState === "hidden") return;
      retryCount = 0;
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        recover();
      }, recoveryDebounceMs);
    };

    recover();

    const pollTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") recover();
    }, statusPollIntervalMs);

    document.addEventListener("visibilitychange", recoverOnResume);
    document.addEventListener("resume", recoverOnResume);
    window.addEventListener("pageshow", recoverOnResume);
    window.addEventListener("focus", recoverOnResume);
    window.addEventListener("online", recoverOnResume);
    window.addEventListener("lawlow:app-resume", recoverOnResume);

    return () => {
      disposed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (debounceTimer) window.clearTimeout(debounceTimer);
      window.clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", recoverOnResume);
      document.removeEventListener("resume", recoverOnResume);
      window.removeEventListener("pageshow", recoverOnResume);
      window.removeEventListener("focus", recoverOnResume);
      window.removeEventListener("online", recoverOnResume);
      window.removeEventListener("lawlow:app-resume", recoverOnResume);
      cleanupStatusSSE();
    };
  }, [
    cleanupStatusSSE,
    fetchQueryStatus,
    navigate,
    shouldMonitor,
    startStatusSSE,
    statusMonitorVersion,
  ]);

  return null;
};

export default PendingQueryMonitor;
